import {useMemoizedValue} from '@sail/react';
import {useContext, useMemo, useRef, useState} from 'react';
import {
  DEFAULT_DEBUG_BOUNDARY_VISIBLE,
  DEFAULT_SET_AND_UNSET_VALUE,
  DEFAULT_START_STOP_VALUE,
} from 'src/internal/constants';
import DebugBoundaryVisibleContext from 'src/internal/provider/DebugBoundaryVisibleContext';
import ObservabilityApiProvider from 'src/internal/provider/ObservabilityApiProvider';
import ObservabilityConfigProvider from 'src/internal/provider/ObservabilityConfigProvider';
import PerformanceMonitorStartStopContext from 'src/internal/provider/PerformanceMonitorStartStopContext';
import PerformanceMonitorTreeContext from 'src/internal/provider/PerformanceMonitorTreeContext';
import DebugBoundary from 'src/public/DebugBoundary';
import sequence from 'src/public/monitor/sequence';
import {v4 as uuidV4} from 'uuid';

import type {ReactElement, ReactNode} from 'react';
import type {ObservabilityConfig} from 'src/types';

type Props = {
  children?: ReactNode;
} & ObservabilityConfig;

/**
 * @see https://sail.stripe.me/apis/observability/ObservabilityProvider
 * @see https://sail.stripe.me/apis/observability/ErrorBoundary
 */
export default function ObservabilityProvider({
  children,
  ...config
}: Props): ReactElement {
  const stableConfig = useMemoizedValue(config);
  const startsRef = useRef(new Set<(id: string, reason?: string) => void>());
  const stopsRef = useRef(new Set<(reason?: string) => void>());
  const debugBoundaryVisibleParent = useContext(DebugBoundaryVisibleContext);
  const setUnsetParent = useContext(PerformanceMonitorTreeContext);
  const startStopParent = useContext(PerformanceMonitorStartStopContext);

  const setUnsetCurrent = useMemo(() => {
    return {
      set(
        start: (id: string, reason?: string | undefined) => void,
        stop: (reason?: string | undefined) => void,
      ): void {
        startsRef.current.add(start);
        stopsRef.current.add(stop);
      },

      unset(
        start: (id: string, reason?: string | undefined) => void,
        stop: (reason?: string | undefined) => void,
      ): void {
        startsRef.current.delete(start);
        stopsRef.current.delete(stop);
      },
    };
  }, []);

  const startStopCurrent = useMemo(() => {
    return {
      start: (reason?: string | undefined): void => {
        const uuid = uuidV4();

        ++sequence.sequence;
        startsRef.current.forEach((start) => start(uuid, reason));
      },

      stop: (reason?: string | undefined): void => {
        ++sequence.sequence;
        stopsRef.current.forEach((stop) => stop(reason));
      },
    };
  }, []);

  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(),
  );

  const debugBoundaryVisibleCurrent = useMemo(
    () => [visibleCategories, setVisibleCategories] as const,
    [visibleCategories],
  );

  const setUnset =
    setUnsetParent === DEFAULT_SET_AND_UNSET_VALUE
      ? setUnsetCurrent
      : setUnsetParent;

  const start =
    startStopParent === DEFAULT_START_STOP_VALUE
      ? startStopCurrent
      : startStopParent;

  const debugBoundaryVisible =
    debugBoundaryVisibleParent === DEFAULT_DEBUG_BOUNDARY_VISIBLE
      ? debugBoundaryVisibleCurrent
      : debugBoundaryVisibleParent;

  return (
    <PerformanceMonitorTreeContext.Provider value={setUnset}>
      <PerformanceMonitorStartStopContext.Provider value={start}>
        <DebugBoundaryVisibleContext.Provider value={debugBoundaryVisible}>
          <ObservabilityConfigProvider config={stableConfig}>
            <ObservabilityApiProvider>
              <DebugBoundary
                category="ObservabilityProvider"
                name={stableConfig.project}
                color="#513DD9"
              >
                {children}
              </DebugBoundary>
            </ObservabilityApiProvider>
          </ObservabilityConfigProvider>
        </DebugBoundaryVisibleContext.Provider>
      </PerformanceMonitorStartStopContext.Provider>
    </PerformanceMonitorTreeContext.Provider>
  );
}
